import { useCallback, useEffect, useMemo, useState } from "react";
import { getClientVaultContext } from "./api";
import type { ClientVaultContext, ClientVaultContextVault } from "./types";

const SELECTED_VAULT_KEY = "ktd_selected_client_vault";

function readStoredVaultId() {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(SELECTED_VAULT_KEY) || "";
}

function storeVaultId(vaultId: string) {
  if (typeof window === "undefined") return;
  if (vaultId) {
    window.localStorage.setItem(SELECTED_VAULT_KEY, vaultId);
  } else {
    window.localStorage.removeItem(SELECTED_VAULT_KEY);
  }
}

export function useClientVaultContext() {
  const [context, setContext] = useState<ClientVaultContext | null>(null);
  const [selectedVaultId, setSelectedVaultId] = useState(readStoredVaultId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setContext(await getClientVaultContext());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load vault context.");
      setContext(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selectedVault = useMemo<ClientVaultContextVault | null>(() => {
    if (!context?.vaults.length) return null;
    return context.vaults.find((vault) => vault.id === selectedVaultId) || context.vaults[0];
  }, [context, selectedVaultId]);

  useEffect(() => {
    if (!context?.vaults.length) {
      storeVaultId("");
      return;
    }
    if (!selectedVaultId || !context.vaults.some((vault) => vault.id === selectedVaultId)) {
      const firstVaultId = context.vaults[0].id;
      setSelectedVaultId(firstVaultId);
      storeVaultId(firstVaultId);
    }
  }, [context, selectedVaultId]);

  const selectVault = useCallback((vaultId: string) => {
    setSelectedVaultId(vaultId);
    storeVaultId(vaultId);
  }, []);

  return {
    context,
    selectedVault,
    selectedVaultId: selectedVault?.id || selectedVaultId,
    loading,
    error,
    refresh,
    selectVault,
  };
}

export function vaultQueryParams(context: ClientVaultContext | null, vault: ClientVaultContextVault | null) {
  if (!context || !vault) return {};
  return {
    clientId: context.client.id,
    eventId: vault.event.id,
    vaultId: vault.id,
    vaultEventId: vault.event.vaultEventId,
  };
}
